// shared-lib/group-customization/roomLinks.js
//
// ONE accessor over the two places a link can live:
//
//   - `room.links[]` — any link with a non-object endpoint (an appliance, a
//     bay, an opening, a wall, a run end) or spanning two fixture groups.
//   - the legacy per-anchor `groupCustomization.links` — links wholly inside
//     one fixture group. The seven server attach sites are pure functions
//     over an objects array and cannot reach `room`, so they keep writing
//     here; consolidating is a later data move, not a behaviour change.
//
// Readers never look at either store directly. See
// reference-docs/layout-relationships/overview.md §2.7.

const { refKey, normalizeRef } = require('./refs');
const { findRunByIdOrAlias, runAliases } = require('../cabinet-runs/runIds');
const { linkKey, endpointKeys } = require('./mergeLinks');
const { pickAnchor } = require('./anchorTypes');

function roomLevel(room) {
  return Array.isArray(room?.links) ? room.links : [];
}

function anchorLevel(room) {
  const out = [];
  for (const o of room?.objects || []) {
    const ls = o?.groupCustomization?.links;
    if (!Array.isArray(ls)) continue;
    for (const l of ls) if (l) out.push(l);
  }
  return out;
}

/** Every endpoint's refKey, fixed refs included. */
function linkEndpointKeys(link) {
  return endpointKeys(link);
}

/** Every link, in either store, with an endpoint equal to `ref`. */
function linksFor(room, ref) {
  const key = refKey(normalizeRef(ref));
  if (!key) return [];
  return [...roomLevel(room), ...anchorLevel(room)].filter((l) =>
    linkEndpointKeys(l).includes(key)
  );
}

/**
 * A fixture group's links: the anchor's legacy list plus every room-level
 * link naming a member or the group itself (`{kind:'appliance'}`).
 */
function groupLinksIn(room, fixtureGroupId) {
  const members = (room?.objects || []).filter(
    (o) => o?.fixtureGroupId === fixtureGroupId
  );
  const anchor = pickAnchor(members);
  // Merge STRAY members' legacy lists too (#1533): before the picker was
  // category-aware, editor saves stamped `groupCustomization` on the SINK —
  // reading only today's anchor made those links silently stop existing.
  // Anchor first, dedupe by id (the anchor's copy wins); the editor's save
  // path clears strays, so this converges to anchor-only.
  const seen = new Set();
  const legacy = [];
  for (const m of [anchor, ...members.filter((x) => x && x !== anchor)]) {
    for (const l of m?.groupCustomization?.links || []) {
      if (!l?.id || seen.has(l.id)) continue;
      seen.add(l.id);
      legacy.push(l);
    }
  }
  const keys = new Set([
    `appliance:${fixtureGroupId}`,
    ...members.map((o) => `object:${o.id}`),
    // A member that claims a run bay is addressed by that bay in links the
    // Center-on apply writes (`subjectRefFor` prefers the bay ref when the
    // stored bay carries no group) — without this key the editor opened
    // from the appliance showed no such link.
    ...members.filter((o) => o.bayId).map((o) => `bay:${o.bayId}`),
  ]);
  const fromRoom = roomLevel(room).filter((l) =>
    linkEndpointKeys(l).some((k) => keys.has(k))
  );
  return [...legacy, ...fromRoom];
}

/** Room-level links naming a run's bays, its ends, or an appliance in it. */
function runLinksIn(room, runId) {
  const run = findRunByIdOrAlias(room?.cabinetRuns, runId);
  const bays = Array.isArray(run?.bays)
    ? run.bays
    : (run?.appliances || []).map((a) => ({
        // The deterministic id `applianceBayFromLegacy` mints — kept in sync
        // by hand because requiring cabinet-runs from here would be a cycle.
        id: a?.id != null ? `bay_${a.id}` : undefined,
        fixtureGroupId: a?.fixtureGroupId,
      }));
  const groupIds = new Set(bays.map((b) => b?.fixtureGroupId).filter(Boolean));
  // Stable bay-id keys (#1526): refs written since bays carry ids key as
  // `bay:<bayId>`, with no run id in the key at all.
  const bayIdKeys = new Set(
    bays.map((b) => (b?.id != null ? `bay:${b.id}` : null)).filter(Boolean)
  );
  // A corner-joined run answers to every wall's per-wall id (#1494).
  const ids = run ? [run.id, ...runAliases(run)] : [runId];
  return roomLevel(room).filter((l) =>
    linkEndpointKeys(l).some(
      (k) =>
        bayIdKeys.has(k) ||
        ids.some(
          (id) => k.startsWith(`bay:${id}:`) || k.startsWith(`run-end:${id}:`)
        ) ||
        (k.startsWith('appliance:') &&
          groupIds.has(k.slice('appliance:'.length)))
    )
  );
}

/** Pure. Dedupes by linkKey; returns `room` by identity when nothing new. */
function addRoomLinks(room, links) {
  const have = new Set(roomLevel(room).map(linkKey));
  const fresh = [];
  for (const l of links || []) {
    if (!l) continue;
    const k = linkKey(l);
    if (have.has(k)) continue;
    have.add(k);
    fresh.push(l);
  }
  if (!fresh.length) return room;
  return { ...room, links: [...roomLevel(room), ...fresh] };
}

/** Pure. Drops one room-level link; legacy anchor lists are not touched. */
function removeRoomLink(room, linkId) {
  const cur = roomLevel(room);
  const next = cur.filter((l) => l?.id !== linkId);
  return next.length === cur.length ? room : { ...room, links: next };
}

/**
 * Drop room-level links whose OBJECT endpoints no longer exist (a reprocess
 * replaces `objects` wholesale and leaves `links` behind). Opening / wall /
 * bay / run-end refs are kept: they resolve lazily against geometry.
 */
function pruneDanglingRoomLinks(room) {
  const cur = roomLevel(room);
  if (!cur.length) return room;
  const ids = new Set((room?.objects || []).map((o) => o?.id).filter(Boolean));
  // Appliance refs dangle too (#1532/M14): a ref naming a fixture group with
  // no surviving member can never act again.
  const groups = new Set(
    (room?.objects || []).map((o) => o?.fixtureGroupId).filter(Boolean)
  );
  const next = cur.filter((l) =>
    linkEndpointKeys(l).every((k) => {
      if (k.startsWith('object:')) return ids.has(k.slice('object:'.length));
      if (k.startsWith('appliance:')) {
        return groups.has(k.slice('appliance:'.length));
      }
      return true;
    })
  );
  return next.length === cur.length ? room : { ...room, links: next };
}

/**
 * The anchor-level twin of `pruneDanglingRoomLinks`: drop every
 * `groupCustomization.links` entry whose OBJECT endpoint is no longer in
 * `objects`. Pure — returns the same array when nothing dangles.
 *
 * A resize that removes an accessory it can't fit leaves its links behind
 * on the anchor, and the within-group editor then opens with a constraint
 * graph pointing at a mirror that doesn't exist (haines Scan 1 bath
 * B88124DF shipped two such links). `pruneDanglingRoomLinks` only sweeps
 * the room-level store, so it never saw them.
 */
function pruneDanglingGroupLinks(objects) {
  if (!Array.isArray(objects) || !objects.length) return objects;
  const ids = new Set(objects.map((o) => o?.id).filter(Boolean));
  let changed = false;
  const next = objects.map((o) => {
    const cur = o?.groupCustomization?.links;
    if (!Array.isArray(cur) || !cur.length) return o;
    const kept = cur.filter((l) =>
      linkEndpointKeys(l).every(
        (k) => !k.startsWith('object:') || ids.has(k.slice('object:'.length))
      )
    );
    if (kept.length === cur.length) return o;
    changed = true;
    return {
      ...o,
      groupCustomization: { ...o.groupCustomization, links: kept },
    };
  });
  return changed ? next : objects;
}

module.exports = {
  linksFor,
  groupLinksIn,
  pruneDanglingGroupLinks,
  runLinksIn,
  addRoomLinks,
  removeRoomLink,
  pruneDanglingRoomLinks,
  linkEndpointKeys,
};
